import { useState, useRef, useEffect, useCallback } from "react";

interface AudioPlayerProps {
  src: string;
  duration?: string;
  variant?: "full" | "compact";
}

function formatTime(seconds: number): string {
  if (!seconds || isNaN(seconds)) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function AudioPlayer({
  src,
  duration,
  variant = "full",
}: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => {
      if (!isDragging) setCurrentTime(audio.currentTime);
    };
    const onDurationChange = () => {
      if (!isNaN(audio.duration)) setTotalDuration(audio.duration);
    };
    const onEnded = () => setIsPlaying(false);
    const onWaiting = () => setIsLoading(true);
    const onCanPlay = () => setIsLoading(false);

    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("durationchange", onDurationChange);
    audio.addEventListener("loadedmetadata", onDurationChange);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("waiting", onWaiting);
    audio.addEventListener("canplay", onCanPlay);

    return () => {
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("durationchange", onDurationChange);
      audio.removeEventListener("loadedmetadata", onDurationChange);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("waiting", onWaiting);
      audio.removeEventListener("canplay", onCanPlay);
    };
  }, [isDragging]);

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      setIsLoading(true);
      try {
        await audio.play();
        setIsPlaying(true);
      } catch {
        setIsLoading(false);
      }
    }
  }, [isPlaying]);

  const seekTo = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const audio = audioRef.current;
      const bar = progressRef.current;
      if (!audio || !bar || !totalDuration) return;
      const rect = bar.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      audio.currentTime = ratio * totalDuration;
      setCurrentTime(ratio * totalDuration);
    },
    [totalDuration]
  );

  const skip = (seconds: number) => {
    const audio = audioRef.current;
    if (!audio || !totalDuration) return;
    audio.currentTime = Math.max(0, Math.min(totalDuration, audio.currentTime + seconds));
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    setVolume(val);
    setIsMuted(val === 0);
    if (audioRef.current) audioRef.current.volume = val;
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isMuted) {
      const v = volume || 0.8;
      audio.volume = v;
      setVolume(v);
      setIsMuted(false);
    } else {
      audio.volume = 0;
      setIsMuted(true);
    }
  };

  const progress = totalDuration ? (currentTime / totalDuration) * 100 : 0;
  const displayDuration = totalDuration ? formatTime(totalDuration) : duration ?? "--:--";

  /* ── COMPACT VARIANT ── */
  if (variant === "compact") {
    return (
      <div className="w-full">
        <audio ref={audioRef} src={src} preload="metadata" />
        <div className="flex items-center gap-3">
          {/* Play button */}
          <button
            onClick={togglePlay}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-amber-500 hover:bg-amber-400 active:scale-95 transition-all duration-200 cursor-pointer flex-shrink-0"
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {isLoading ? (
              <i className="ri-loader-4-line text-amber-950 text-base animate-spin" />
            ) : isPlaying ? (
              <i className="ri-pause-fill text-amber-950 text-base" />
            ) : (
              <i className="ri-play-fill text-amber-950 text-base ml-0.5" />
            )}
          </button>

          {/* Progress + times */}
          <div className="flex-1 min-w-0">
            <div
              ref={progressRef}
              onClick={seekTo}
              className="relative h-1.5 bg-amber-100 rounded-full cursor-pointer group mb-1.5 overflow-visible"
            >
              <div
                className="absolute left-0 top-0 h-full bg-amber-500 rounded-full"
                style={{ width: `${progress}%` }}
              />
              <div
                className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-amber-500 rounded-full -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ left: `${progress}%` }}
              />
            </div>
            <div className="flex justify-between font-mono text-[10px] text-stone-400">
              <span className="text-amber-600 font-semibold">{formatTime(currentTime)}</span>
              <span>{displayDuration}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ── FULL VARIANT ── */
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-6">
      <audio ref={audioRef} src={src} preload="metadata" />

      {/* Progress bar */}
      <div
        ref={progressRef}
        onClick={seekTo}
        className="relative h-2 bg-amber-200 rounded-full cursor-pointer group mb-4 overflow-visible"
      >
        {/* Filled */}
        <div
          className="absolute left-0 top-0 h-full bg-amber-500 rounded-full transition-all duration-100"
          style={{ width: `${progress}%` }}
        />
        {/* Thumb */}
        <div
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-4 h-4 bg-amber-500 rounded-full border-2 border-white opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ left: `${progress}%` }}
        />
      </div>

      {/* Time stamps */}
      <div className="flex justify-between font-mono text-xs mb-5">
        <span className="text-amber-600 font-semibold">{formatTime(currentTime)}</span>
        <span className="text-stone-400">{displayDuration}</span>
      </div>

      {/* Controls row */}
      <div className="flex items-center gap-3">
        {/* Skip back */}
        <button
          onClick={() => skip(-15)}
          className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-amber-100 text-stone-400 hover:text-amber-700 transition-all cursor-pointer"
          aria-label="Rewind 15 seconds"
          title="Rewind 15s"
        >
          <i className="ri-replay-15-line text-lg" />
        </button>

        {/* Play / Pause */}
        <button
          onClick={togglePlay}
          className="w-14 h-14 flex items-center justify-center rounded-full bg-amber-500 hover:bg-amber-400 active:scale-95 transition-all duration-200 cursor-pointer flex-shrink-0"
          aria-label={isPlaying ? "Pause" : "Play"}
        >
          {isLoading ? (
            <i className="ri-loader-4-line text-amber-950 text-2xl animate-spin" />
          ) : isPlaying ? (
            <i className="ri-pause-fill text-amber-950 text-2xl" />
          ) : (
            <i className="ri-play-fill text-amber-950 text-2xl ml-1" />
          )}
        </button>

        {/* Skip forward */}
        <button
          onClick={() => skip(15)}
          className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-amber-100 text-stone-400 hover:text-amber-700 transition-all cursor-pointer"
          aria-label="Skip 15 seconds"
          title="Skip 15s"
        >
          <i className="ri-forward-15-line text-lg" />
        </button>

        {/* Volume — desktop only */}
        <div className="hidden sm:flex items-center gap-2 ml-auto">
          <button
            onClick={toggleMute}
            className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-amber-600 transition-colors cursor-pointer"
            aria-label={isMuted ? "Unmute" : "Mute"}
          >
            <i
              className={`text-base ${
                isMuted || volume === 0
                  ? "ri-volume-mute-line"
                  : volume < 0.5
                  ? "ri-volume-down-line"
                  : "ri-volume-up-line"
              }`}
            />
          </button>
          <input
            type="range"
            min="0"
            max="1"
            step="0.02"
            value={isMuted ? 0 : volume}
            onChange={handleVolumeChange}
            className="w-20 accent-amber-500 cursor-pointer"
            aria-label="Volume"
          />
        </div>
      </div>
    </div>
  );
}
